import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Button from "@/components/Button";
import MenuDropdown from "@/components/MenuDropdown";
import Dialog from "@/components/Dialog";
import Banner from "@/components/Banner";
import classNames from "classnames";
import type { UniversityStaffMember, UniversityStaffRole } from "./types";

interface EditStaffRoleModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (role: UniversityStaffRole) => void;
  staffMember: UniversityStaffMember | null;
  isLoading?: boolean;
}

interface FormValues {
  role: UniversityStaffRole;
}

const ROLE_OPTIONS: Array<{
  value: UniversityStaffRole;
  label: string;
  description: string;
}> = [
  {
    value: "DIRECTOR",
    label: "Director",
    description:
      "Directors receive university event registration notifications in addition to roster, probation, and graduation emails."
  },
  {
    value: "STAFF",
    label: "Staff",
    description:
      "Staff receive roster finalized, probation, and graduation finalized notifications for this university."
  }
];

export function EditStaffRoleModal({ open, onClose, onSave, staffMember, isLoading = false }: EditStaffRoleModalProps) {
  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: {
      role: staffMember?.role || "STAFF"
    }
  });

  const selectedRole = watch("role");

  useEffect(() => {
    if (staffMember) {
      reset({ role: staffMember.role });
    }
  }, [staffMember, reset]);

  const onSubmit = (values: FormValues) => {
    onSave(values.role);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Change Role"
      className="sm:max-w-[480px]"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading}
            icon={isLoading ? "progress_activity" : undefined}
            iconClassName={classNames(isLoading && "animate-spin w-6 h-6 !block")}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <p className="text-body-medium text-sys-on-surface">
          Update the role for {staffMember?.user.name || "this staff member"}.
        </p>

        <Controller
          name="role"
          control={control}
          rules={{ required: "Role is required" }}
          render={({ field: { onChange, value }, fieldState }) => (
            <div>
              <MenuDropdown
                label="Role"
                options={ROLE_OPTIONS}
                value={ROLE_OPTIONS.find((option) => option.value === value)}
                onSelect={(option) => onChange(option.value)}
                getLabel={(option) => option.label}
                renderItem={(option) => <div className="text-body-large">{option.label}</div>}
                constrainMenuWidth
                disabled={isLoading}
              />
              {fieldState.invalid && fieldState.error?.message && (
                <div className="text-sys-brand-error text-body-small pt-1 pl-4">{fieldState.error.message}</div>
              )}
            </div>
          )}
        />

        {selectedRole && (
          <Banner
            variant="info"
            inline
            description={ROLE_OPTIONS.find((option) => option.value === selectedRole)?.description || ""}
          />
        )}
      </div>
    </Dialog>
  );
}
